import { makeDefaultDrawing, propertyLineX, rcCounterfortPositions, rcCounterfortProfile, rcFrame, type Pt, type WallInput, type WallResult } from "@tasduvar/engine";
import { backfillSurface, dimensionLines, sceneBox } from "../../features/drawing/geometry";
import { rebarItems } from "../../features/drawing/rebar";
import { DxfWriter, encodeDxf, type DimStyleDef, type PatternLine } from "./writer";
import { MOLOZ } from "./patterns";
import { applyEdits, assignKeys, dimGeom, followLeaders, type SheetEdits } from "./edits";
import { declutterTexts } from "./declutter";
export { encodeDxf };

/**
 * Duvar paftası (AutoCAD 2000 DXF): kesit (tarama, barbakan, drenaj borusu + filtre + geotekstil, grobeton, harpuşta, kot işaretleri, ölçü
 * zincirleri) · barbakan yerleşim planı (ön görünüş; ano derzleri) · betonarmede donatı kesiti + poz tablosu · notlar + antet.
 * Model birimi cm (Türk pratiği) ya da m; ölçek yalnız yazı/ok boyutunu belirler (2,5 mm yazı × ölçek).
 * Kaynaklar: KGM Teknik Şartname Şekil-307-3, KGM Tasarım El Kitabı Şekil 4.15, DSİ taşkın duvarı şartnamesi §8–10; renk/başlık/doku
 * dili Cem'in istCAD örnek paftalarıyla (2026-09-22, docs/research/10 §1.7) hizalandı: sarı duvar çizgisi, kırmızı ölçü, yeşil yazı,
 * mavi dolu kot üçgeni, gri moloz taş taraması (örnek DWG'deki AGGREGAT deseni), "%%U" altı çizili "A-A DUVAR KESİTİ — ÖLÇEK 1/n" başlık +
 * "Duvar kesit alanı", ön görünüşte A-A kesit işaretleri, gizli çizgili barbakan boruları + "%n eğimli / Ø" etiketleri, pafta çerçevesi.
 */
export interface DxfOptions {
  unit: "cm" | "m";
  scale: number;                                   // 1:scale (yazı yüksekliği 2,5 mm × ölçek)
  barbacane: { enabled: boolean; diameter: number; hSpacing: number; vSpacing: number; startHeight: number; angleDeg: number };   // mm, m, m, m (ön zeminden), °
  drain: { enabled: boolean; diameter: number };  // mm
  filterThickness: number;                         // m (0 = yok)
  blinding: { thickness: number; overhang: number };   // m
  coping: { enabled: boolean; height: number; overhang: number };   // harpuşta (m)
  anoLength: number;                               // m (dilatasyon / ano derzi aralığı)
  wallLength: number;                              // m (ön görünüş)
  stone: "moloz" | "yonu";                         // taş dokusu: moloz (AGGREGAT taraması) / kaba yonu (şaşırtmalı sıra taraması)
  levelRef: "ground" | "base" | "top";             // kot işaretlerinin sıfırı: ön zemin (D_f) · taban · tepe
  levelDatum: number;                              // referansın mutlak kotu (m); 0 = göreli (±0,00)
  /** Ölçü stili: kâğıtta yazı / ok boyu (mm), ondalık (yoksa cm → 0, m → 2) */
  dim?: { textMm: number; arrowMm: number; decimals?: number; arrow?: "closed" | "tick" };
  /** Kullanıcı katmanları (pafta katman yöneticisi) */
  layers?: { name: string; color: number }[];
  projectName?: string;
  date?: string;
  digest?: string;
}

/**
 * Duvarın "Çizim" ayarlarından (`wall.drawing`) pafta seçenekleri; boş bırakılan alanlar Metraj ve Malzeme sekmesinden türetilir
 * (barbakan/drenaj çapı ve düşey aralık, filtre kalınlığı, taş dokusu), grobeton ise her zaman metrajdan.
 */
export function defaultDxfOptions(wall: WallInput): DxfOptions {
  const q = wall.quantities;
  const d = wall.drawing;
  const on = (show: "auto" | "on" | "off"): boolean => (show === "auto" ? q.drainage.enabled : show === "on");
  const barbOn = on(d.barbacane.show);
  return {
    unit: d.unit, scale: d.scale,
    barbacane: { enabled: barbOn, diameter: d.barbacane.diameter ?? q.drainage.barbacaneDiameter, hSpacing: d.barbacane.hSpacing, vSpacing: d.barbacane.vSpacing ?? q.drainage.barbacaneSpacing, startHeight: d.barbacane.startHeight, angleDeg: d.barbacane.angleDeg },
    drain: { enabled: on(d.drain.show), diameter: d.drain.diameter ?? q.drainage.pipeDiameter },
    filterThickness: d.filterThickness ?? (q.drainage.enabled ? q.drainage.filterThickness : 0),
    blinding: { thickness: q.blindingThickness, overhang: q.blindingOverhang },
    coping: { enabled: d.coping.enabled && wall.geometry.mode !== "rc-cantilever", height: d.coping.height, overhang: d.coping.overhang },
    anoLength: d.anoLength, wallLength: wall.length ?? 10,
    stone: d.stone === "auto" ? (/yonu|kesme/i.test(wall.material.description ?? "") ? "yonu" : "moloz") : d.stone,
    levelRef: d.levelRef, levelDatum: d.levelDatum,
    dim: d.sheet?.dimStyle, layers: d.sheet?.layers ?? [],
  };
}

// Katmanlar (ad · ACI renk · çizgi tipi · kalınlık 1/100 mm). Renk dili istCAD örnekleriyle aynı: duvar sarı (2), ölçü kırmızı (1),
// yazı/başlık yeşil (3), kot mavi (5), tarama gri (8), çerçeve kırmızı.
export const LAYERS = [
  { name: "DUVAR", color: 2, lineweight: 50 }, { name: "TEMEL", color: 2, lineweight: 50 }, { name: "GROBETON", color: 8, lineweight: 25 },
  { name: "TARAMA", color: 8, lineweight: 9 }, { name: "ZEMIN", color: 7, lineweight: 35 }, { name: "SU", color: 5, ltype: "DASHED", lineweight: 18 },
  { name: "DRENAJ", color: 5, lineweight: 25 }, { name: "GEOTEKSTIL", color: 5, ltype: "DASHED", lineweight: 18 }, { name: "BARBAKAN", color: 4, lineweight: 25 },
  { name: "DERZ", color: 6, ltype: "DASHDOT", lineweight: 18 }, { name: "HARPUSTA", color: 2, lineweight: 35 }, { name: "DONATI", color: 1, lineweight: 35 },
  { name: "OLCU", color: 1, lineweight: 13 }, { name: "KOT", color: 5, lineweight: 13 }, { name: "YAZI", color: 3, lineweight: 18 }, { name: "NOT", color: 3, lineweight: 13 },
  { name: "ANTET", color: 1, lineweight: 35 }, { name: "GIZLI", color: 8, ltype: "HIDDEN", lineweight: 13 }, { name: "PAYANDA", color: 4, lineweight: 35 },
  { name: "PARSEL", color: 1, ltype: "DASHDOT", lineweight: 25 },
  { name: "KULLANICI", color: 7, lineweight: 25 },   // paftada elle çizilen çizgi ve yazılar
] as const;

/** Katman / renk seçimlerinde sunulan ACI renkleri */
export const ACI_NAMES: [number, string][] = [[1, "Kırmızı"], [2, "Sarı"], [3, "Yeşil"], [4, "Camgöbeği"], [5, "Mavi"], [6, "Eflatun"], [7, "Siyah / beyaz"], [8, "Gri"]];
/** Kullanıcı katmanı adı: AutoCAD'in yasakladığı karakterler (<>/\":;?*|,=`) atılır, büyük harf, en çok 40 karakter; boşsa ya da varsa null */
export function layerName(raw: string, existing: string[]): string | null {
  const n = raw.replace(/[<>/\\":;?*|,=`]/g, "").trim().toLocaleUpperCase("tr").slice(0, 40);
  if (!n || [...LAYERS.map((l) => l.name), ...existing].some((x) => x.toLocaleUpperCase("tr") === n)) return null;
  return n;
}

// Tarama desenleri (cm biriminde; m için 0,01 ölçek)
const PATTERNS: Record<string, PatternLine[]> = {
  BETON: [{ angle: 45, x: 0, y: 0, dx: 0, dy: 8 }],
  KARGIR: [{ angle: 0, x: 0, y: 0, dx: 0, dy: 25 }, { angle: 90, x: 0, y: 0, dx: 25, dy: 40, dashes: [25, -25] }],   // 40×25 şaşırtmalı kaba yonu
  MOLOZ,
  ZEMIN: [{ angle: 0, x: 0, y: 0, dx: 5, dy: 5, dashes: [5, -5] }, { angle: 90, x: 1.875, y: 3.125, dx: 5, dy: 5, dashes: [5, -5] }],   // AutoCAD EARTH ×20
  GROBETON: [{ angle: 0, x: 0, y: 0, dx: 3, dy: 3, dashes: [0.6, -5.4] }, { angle: 0, x: 3, y: 1.5, dx: 3, dy: 3, dashes: [0.6, -5.4] }],
  KUM: [{ angle: 0, x: 0, y: 0, dx: 2, dy: 2, dashes: [0.4, -3.6] }],
};

const fmtCm = (m: number): string => String(Math.round(m * 100));
const fmt2 = (v: number): string => v.toFixed(2).replace(".", ",");

/** Çokgen kenarlarının y yatayıyla kesişim x'leri */
function crossingsAt(poly: Pt[], y: number): number[] {
  const xs: number[] = [];
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]!, b = poly[(i + 1) % poly.length]!;
    if ((a.y <= y && b.y > y) || (b.y <= y && a.y > y)) xs.push(a.x + ((y - a.y) * (b.x - a.x)) / (b.y - a.y));
    else if (Math.abs(a.y - y) < 1e-9 && Math.abs(b.y - y) < 1e-9) xs.push(a.x, b.x);
  }
  return xs;
}
const xFrontAt = (poly: Pt[], y: number): number => Math.min(...crossingsAt(poly, y));
const xBackAt = (poly: Pt[], y: number): number => Math.max(...crossingsAt(poly, y));
/** Işının çokgen kenarlarıyla ilk kesişimi */
function rayHit(poly: Pt[], p: Pt, d: Pt): Pt | null {
  let best: { t: number; p: Pt } | null = null;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]!, b = poly[(i + 1) % poly.length]!, ex = b.x - a.x, ey = b.y - a.y;
    const den = d.x * ey - d.y * ex;
    if (Math.abs(den) < 1e-12) continue;
    const t = ((a.x - p.x) * ey - (a.y - p.y) * ex) / den, s = ((a.x - p.x) * d.y - (a.y - p.y) * d.x) / den;
    if (t > 1e-6 && s >= -1e-9 && s <= 1 + 1e-9 && (!best || t < best.t)) best = { t, p: { x: p.x + t * d.x, y: p.y + t * d.y } };
  }
  return best?.p ?? null;
}
const polyLength = (pts: Pt[]): number => pts.reduce((s, p, i) => (i ? s + Math.hypot(p.x - pts[i - 1]!.x, p.y - pts[i - 1]!.y) : 0), 0);

type Extra = { ltype?: string; color?: number; lineweight?: number };
type TextOpts = { h?: number; rotation?: number; halign?: 0 | 1 | 2; valign?: 0 | 1 | 2 | 3; color?: number };
/** Pafta öğesi (metre, model koordinatı). DXF de ekran önizlemesi de bu listeden üretilir → ikisi birebir aynı. */
export type SheetPrim = ({ k: "line"; g: string; layer: string; a: Pt; b: Pt; extra?: Extra }
  | { k: "poly"; g: string; layer: string; pts: Pt[]; closed: boolean; extra?: Extra }
  | { k: "circle"; g: string; layer: string; c: Pt; r: number }
  | { k: "text"; g: string; layer: string; p: Pt; value: string; opts: TextOpts & { h: number } }
  | { k: "mtext"; g: string; layer: string; p: Pt; width: number; lines: string[]; h: number }
  | { k: "hatch"; g: string; pattern: string | null; loops: Pt[][]; color?: number }
  | { k: "dim"; g: string; a: Pt; b: Pt; offset: number; text?: string; /** yön (yoksa a.x = b.x ise düşey) */ dir?: "h" | "v"; layer?: string }
  | { k: "solid"; g: string; layer: string; pts: Pt[] }) & {
    /** kararlı anahtar (blok:tür:sıra) ve parmak izi — elle düzenleme için */ key?: string; fp?: string;
    /** Leader parçası: aynı `id`li kol (ok ucundan dirseğe), sahanlık çizgisi ve yazı — yazı taşınınca çizgiler izler (`followLeaders`) */
    lead?: { id: string; role: "arm" | "land" | "text" };
  };

/** Metre koordinatlarıyla çalışan pafta: öğeleri görünüm bloklarına (`group`) ayırarak kaydeder; `emit` çizim birimine (cm/m) ölçekleyip DXF'e yazar. */
class Sheet {
  readonly u: number;
  readonly th: number;                             // yazı yüksekliği (m)
  readonly dim: DimStyleDef;
  readonly dimTh: number;                          // ölçü yazısı yüksekliği (m)
  readonly dimArrow: number;                       // ölçü oku boyu (m)
  readonly prims: SheetPrim[] = [];
  group = "kesit";
  constructor(readonly o: DxfOptions) {
    this.u = o.unit === "cm" ? 100 : 1;
    this.th = 0.0025 * o.scale;
    this.dimTh = ((o.dim?.textMm ?? 2.5) / 1000) * o.scale;
    this.dimArrow = ((o.dim?.arrowMm ?? 2.5) / 1000) * o.scale;
    this.dim = { name: "TASDUVAR", textHeight: this.dimTh * this.u, arrowSize: this.dimArrow * this.u, extOffset: 0.3 * this.dimTh * this.u, extExtend: 0.5 * this.dimTh * this.u, decimals: o.dim?.decimals ?? (o.unit === "cm" ? 0 : 2), tick: o.dim?.arrow === "tick" };
  }
  line(layer: string, a: Pt, b: Pt, extra?: Extra): void { this.prims.push({ k: "line", g: this.group, layer, a, b, extra }); }
  poly(layer: string, pts: Pt[], closed: boolean, extra?: Extra): void { this.prims.push({ k: "poly", g: this.group, layer, pts, closed, extra }); }
  circle(layer: string, c: Pt, r: number): void { this.prims.push({ k: "circle", g: this.group, layer, c, r }); }
  text(layer: string, p: Pt, value: string, opts: TextOpts = {}): void { this.prims.push({ k: "text", g: this.group, layer, p, value, opts: { ...opts, h: opts.h ?? this.th } }); }
  mtext(layer: string, p: Pt, width: number, lines: string[], h = this.th * 0.8): void { this.prims.push({ k: "mtext", g: this.group, layer, p, width, lines, h }); }
  hatch(pattern: keyof typeof PATTERNS | null, loops: Pt[][], color?: number): void { this.prims.push({ k: "hatch", g: this.group, pattern, loops, color }); }
  dimension(a: Pt, b: Pt, offset: number, text?: string): void { this.prims.push({ k: "dim", g: this.group, a, b, offset, text }); }
  solid(layer: string, pts: Pt[]): void { this.prims.push({ k: "solid", g: this.group, layer, pts }); }
  /** Kot işareti: içi boş üçgen + yatay çizgi + "+3,00" */
  level(p: Pt, value: number, side: "left" | "right" = "left"): void {
    const s = this.th * 0.8, dir = side === "left" ? -1 : 1;
    // Dolu üçgen (örnek DWG: GokKotlar SOLID) + çizgi + değer
    this.solid("KOT", [p, { x: p.x + dir * s * 0.6, y: p.y + s }, { x: p.x - dir * s * 0.6, y: p.y + s }]);
    this.line("KOT", { x: p.x - dir * s * 0.6, y: p.y + s }, { x: p.x + dir * s * 3.6, y: p.y + s });
    const label = `${value > 1e-9 ? "+" : value < -1e-9 ? "−" : "±"}${fmt2(Math.abs(value))}`;
    this.text("KOT", { x: p.x + dir * s * 0.8, y: p.y + s * 1.2 }, label, { h: this.th * 0.9, halign: side === "left" ? 2 : 0 });
  }
  /** Ok uçlu etiket çizgisi (leader) */
  leader(from: Pt, to: Pt, label: string, layer = "YAZI"): void {
    const id = `${this.group}:L${++this.leaders}`;
    const tag = (role: "arm" | "land" | "text"): void => { this.prims[this.prims.length - 1]!.lead = { id, role }; };
    this.line(layer, from, to, { lineweight: 13 }); tag("arm");
    const dx = Math.sign(to.x - from.x) || 1;
    this.line(layer, to, { x: to.x + dx * this.th * 2, y: to.y }, { lineweight: 13 }); tag("land");
    this.text(layer, { x: to.x + dx * this.th * 2.3, y: to.y + this.th * 0.25 }, label, { h: this.th * 0.9, halign: dx > 0 ? 0 : 2 }); tag("text");
  }
  private leaders = 0;
  /** Kayıtlı öğeleri DXF'e yazar; `shift` blok kaydırması (m), `skip` gizli bloklar. */
  emit(shift: (g: string) => Pt, skip: (g: string) => boolean): string {
    const w = new DxfWriter(this.o.unit === "cm" ? 5 : 6, 0.2 * this.o.scale * (this.o.unit === "cm" ? 1 : 0.01));   // 1/50 cm → 10: HIDDEN 6 cm çizgi ≈ 1,2 mm kâğıtta
    for (const L of LAYERS) w.addLayer(L);
    for (const L of this.o.layers ?? []) w.addLayer({ ...L, lineweight: 25 });
    w.addDimStyle(this.dim);
    for (const e of this.prims) {
      if (skip(e.g)) continue;
      const d = shift(e.g);
      const P = (p: Pt): Pt => ({ x: (p.x + d.x) * this.u, y: (p.y + d.y) * this.u });
      switch (e.k) {
        case "line": w.line(e.layer, P(e.a), P(e.b), e.extra); break;
        case "poly": w.polyline(e.layer, e.pts.map(P), e.closed, e.extra); break;
        case "circle": w.circle(e.layer, P(e.c), e.r * this.u); break;
        case "text": w.text(e.layer, P(e.p), e.opts.h * this.u, e.value, e.opts); break;
        case "mtext": w.mtext(e.layer, P(e.p), e.h * this.u, e.width * this.u, e.lines); break;
        case "hatch": w.hatch("TARAMA", e.loops.map((L) => L.map(P)), e.pattern ? { name: e.pattern, lines: PATTERNS[e.pattern]!, scale: this.u / 100 } : null, { color: e.color }); break;
        case "dim": w.dimLinear(P(e.a), P(e.b), e.offset * this.u, { style: this.dim, text: e.text, vertical: e.dir ? e.dir === "v" : undefined, layer: e.layer }); break;
        case "solid": w.solid(e.layer, e.pts.map(P) as [Pt, Pt, Pt]); break;
      }
    }
    return w.toString();
  }
}

/** Pafta blokları (sürüklenebilir görünümler) */
export const SHEET_BLOCKS: { id: string; label: string }[] = [
  { id: "kesit", label: "A-A kesiti" }, { id: "gorunus", label: "Önden görünüş ve barbakanlar" }, { id: "plan", label: "Plan (payandalar)" },
  { id: "donati", label: "Donatı kesiti ve tablosu" }, { id: "notlar", label: "Genel notlar" }, { id: "antet", label: "Antet" },
];
/** Kâğıt boyutları (mm, yatay) */
export const PAPER_MM: Record<"A4" | "A3" | "A2" | "A1" | "A0", { w: number; h: number }> = { A4: { w: 297, h: 210 }, A3: { w: 420, h: 297 }, A2: { w: 594, h: 420 }, A1: { w: 841, h: 594 }, A0: { w: 1189, h: 841 } };
export interface Box { minX: number; minY: number; maxX: number; maxY: number }
export interface SheetLayout {
  prims: SheetPrim[];
  /** Blokların kaydırmasız kutusu ve geçerli kaydırması (m) */
  blocks: { id: string; label: string; box: Box; offset: Pt; hidden: boolean }[];
  frame: Box;
  paperMm: { w: number; h: number };
  scale: number; unit: "cm" | "m"; th: number; dimDecimals: number;
  /** Ölçü stili (m): yazı yüksekliği ve ok boyu */
  dimTh: number; dimArrow: number; dimTick: boolean;
  /** Kullanıcı katmanları (standart katmanlara ek) */
  layers: { name: string; color: number }[];
  /** Duvar değiştiği için uygulanamayan elle düzenleme sayısı (parmak izi tutmadı) */
  staleEdits: number;
  /** Kot atma: model y (m) → kot = datum + (y − zero); kesit ve görünüş aynı düşey başlangıçta çizilir */
  level: { zero: number; datum: number };
}

/** Duvar paftası: öğeleri bloklara ayırır, kâğıt/kaydırma ayarlarını uygular, çerçeve + antet yerleşimini hesaplar. */
export function buildSheet(wall: WallInput, result: WallResult, options?: Partial<DxfOptions>): SheetLayout {
  const { sh, yZero } = composeSheet(wall, result, options);
  const o = sh.o;
  const set = wall.drawing.sheet ?? makeDefaultDrawing().sheet;
  const present = SHEET_BLOCKS.filter((b) => sh.prims.some((e) => e.g === b.id));
  const boxes = new Map(present.map((b) => [b.id, primsBox(sh.prims.filter((e) => e.g === b.id), sh)]));
  const hidden = new Set(set.hidden);
  const offsets = new Map<string, Pt>();
  for (const b of present) { const u = set.offsets[b.id]; offsets.set(b.id, u ? { x: u.dx, y: u.dy } : { x: 0, y: 0 }); }
  const moved = (id: string): Box => { const b = boxes.get(id)!, d = offsets.get(id)!; return { minX: b.minX + d.x, minY: b.minY + d.y, maxX: b.maxX + d.x, maxY: b.maxY + d.y }; };
  const union = (ids: string[]): Box | null => ids.reduce<Box | null>((acc, id) => { const b = moved(id); return acc ? { minX: Math.min(acc.minX, b.minX), minY: Math.min(acc.minY, b.minY), maxX: Math.max(acc.maxX, b.maxX), maxY: Math.max(acc.maxY, b.maxY) } : b; }, null);
  const visible = present.filter((b) => !hidden.has(b.id)).map((b) => b.id);
  let frame: Box, paperMm: { w: number; h: number };
  if (set.paper === "auto") {
    const u = union(visible) ?? { minX: 0, minY: 0, maxX: 1, maxY: 1 };
    frame = { minX: u.minX - 0.6, minY: u.minY - 0.6, maxX: u.maxX + 0.6, maxY: u.maxY + 0.6 };
    paperMm = { w: ((frame.maxX - frame.minX) * 1000) / o.scale, h: ((frame.maxY - frame.minY) * 1000) / o.scale };
  } else {
    const p = PAPER_MM[set.paper];
    paperMm = set.orientation === "portrait" ? { w: p.h, h: p.w } : p;
    const W = (paperMm.w * o.scale) / 1000, H = (paperMm.h * o.scale) / 1000, m = (10 * o.scale) / 1000;
    const views = union(visible.filter((id) => id !== "notlar" && id !== "antet")) ?? union(visible) ?? { minX: 0, minY: 0, maxX: 1, maxY: 1 };
    frame = { minX: views.minX - m, maxX: views.minX - m + W, maxY: views.maxY + m, minY: views.maxY + m - H };
    // Kullanıcı taşımadıysa: antet sağ alt köşeye, notlar sol alta (AutoCAD paftası düzeni)
    const snap = (id: string, x: number, y: number, corner: "br" | "bl"): void => {
      if (!boxes.has(id) || set.offsets[id]) return;
      const b = boxes.get(id)!;
      offsets.set(id, corner === "br" ? { x: x - b.maxX, y: y - b.minY } : { x: x - b.minX, y: y - b.minY });
    };
    snap("antet", frame.maxX - m, frame.minY + m, "br");
    snap("notlar", frame.minX + m, frame.minY + m, "bl");
  }
  const frameId = "cerceve";
  sh.group = frameId;
  sh.poly("ANTET", [{ x: frame.minX, y: frame.minY }, { x: frame.maxX, y: frame.minY }, { x: frame.maxX, y: frame.maxY }, { x: frame.minX, y: frame.maxY }], true, { lineweight: 50 });
  // Ölçekle dondurulan ölçü metni: kaydırma öncesi ölçü değeri (cm/m, pafta ondalığıyla)
  const u = o.unit === "cm" ? 100 : 1;
  const dimLabel = (e: Extract<SheetPrim, { k: "dim" }>): string => (dimGeom(e).measure * u).toFixed(sh.dim.decimals).replace(".", ",");
  const keyed = assignKeys(sh.prims);
  const shifted = keyed.filter((e) => !hidden.has(e.g)).map((e) => (e.g === frameId ? e : shiftPrim(e, offsets.get(e.g) ?? { x: 0, y: 0 })));
  // Blok içinde üst üste binen yazılar kaydırılır (kot/ölçü/tablo/not engel, taşınmaz); elle düzenlemeler bunun üstüne uygulanır
  const clean = declutterTexts(shifted, sh.th, dimLabel);
  return {
    prims: followLeaders(shifted, applyEdits(clean, set.edits as SheetEdits | undefined, dimLabel), set.edits as SheetEdits | undefined),
    blocks: present.map((b) => ({ ...b, box: boxes.get(b.id)!, offset: offsets.get(b.id)!, hidden: hidden.has(b.id) })),
    frame, paperMm, scale: o.scale, unit: o.unit, th: sh.th, dimDecimals: sh.dim.decimals, dimTh: sh.dimTh, dimArrow: sh.dimArrow, dimTick: !!sh.dim.tick, layers: o.layers ?? [], level: { zero: yZero, datum: o.levelDatum },
    staleEdits: Object.entries((set.edits as SheetEdits | undefined)?.overrides ?? {}).filter(([k, ov]) => !keyed.some((e) => e.key === k && e.fp === ov.fp)).length,
  };
}

/** Paftayı AutoCAD 2000 DXF metnine çevirir (blok kaydırmaları ve gizli bloklar uygulanmış). */
export function buildDxf(wall: WallInput, result: WallResult, options?: Partial<DxfOptions>): string {
  const L = buildSheet(wall, result, options);
  const sh = new Sheet({ ...defaultDxfOptions(wall), ...options });
  sh.prims.push(...L.prims);
  return sh.emit(() => ({ x: 0, y: 0 }), () => false);
}

function shiftPrim(e: SheetPrim, d: Pt): SheetPrim {
  if (!d.x && !d.y) return e;
  const P = (p: Pt): Pt => ({ x: p.x + d.x, y: p.y + d.y });
  switch (e.k) {
    case "line": return { ...e, a: P(e.a), b: P(e.b) };
    case "poly": return { ...e, pts: e.pts.map(P) };
    case "circle": return { ...e, c: P(e.c) };
    case "text": case "mtext": return { ...e, p: P(e.p) };
    case "hatch": return { ...e, loops: e.loops.map((L) => L.map(P)) };
    case "dim": return { ...e, a: P(e.a), b: P(e.b) };
    case "solid": return { ...e, pts: e.pts.map(P) };
  }
}

/** Yaklaşık blok kutusu (yazı genişliği ≈ 0,6·h·karakter; MTEXT sarma dâhil) */
function primsBox(prims: SheetPrim[], sh: Sheet): Box {
  const b: Box = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
  const add = (x: number, y: number): void => { b.minX = Math.min(b.minX, x); b.minY = Math.min(b.minY, y); b.maxX = Math.max(b.maxX, x); b.maxY = Math.max(b.maxY, y); };
  for (const e of prims) {
    if (e.k === "line") { add(e.a.x, e.a.y); add(e.b.x, e.b.y); }
    else if (e.k === "poly" || e.k === "solid") e.pts.forEach((p) => add(p.x, p.y));
    else if (e.k === "hatch") e.loops.forEach((L) => L.forEach((p) => add(p.x, p.y)));
    else if (e.k === "circle") { add(e.c.x - e.r, e.c.y - e.r); add(e.c.x + e.r, e.c.y + e.r); }
    else if (e.k === "text") {
      const w = textWidth(e.value, e.opts.h), ha = e.opts.halign ?? 0;
      const x0 = ha === 1 ? e.p.x - w / 2 : ha === 2 ? e.p.x - w : e.p.x;
      if (e.opts.rotation && Math.abs(e.opts.rotation) > 45) { add(e.p.x - e.opts.h, e.p.y); add(e.p.x + e.opts.h, e.p.y + w); }
      else { add(x0, e.p.y - e.opts.h * 0.3); add(x0 + w, e.p.y + e.opts.h); }
    } else if (e.k === "mtext") { const n = wrapLines(e.lines, e.width, e.h).length; add(e.p.x, e.p.y); add(e.p.x + e.width, e.p.y - e.h * 1.5 * n); }
    else if (e.k === "dim") {
      const d = dimGeom(e), o2 = Math.sign(e.offset || 1) * sh.th * 1.4;
      add(e.a.x, e.a.y); add(e.b.x, e.b.y);
      if (d.vertical) { add(d.p1.x + o2, d.p1.y); add(d.p2.x + o2, d.p2.y); } else { add(d.p1.x, d.p1.y + o2); add(d.p2.x, d.p2.y + o2); }
    }
  }
  return Number.isFinite(b.minX) ? b : { minX: 0, minY: 0, maxX: 0, maxY: 0 };
}
export const textWidth = (value: string, h: number): number => value.replace(/%%U/g, "").length * h * 0.6;
/** MTEXT satırlarını genişliğe göre sarar (önizleme ve kutu hesabı; AutoCAD sarmasıyla yaklaşık aynı). */
export function wrapLines(lines: string[], width: number, h: number): string[] {
  const max = Math.max(8, Math.floor(width / (h * 0.6)));
  const out: string[] = [];
  for (const line of lines) {
    let cur = "";
    for (const word of line.split(" ")) { if ((cur + " " + word).trim().length > max && cur) { out.push(cur); cur = word; } else cur = (cur + " " + word).trim(); }
    out.push(cur);
  }
  return out;
}

function composeSheet(wall: WallInput, result: WallResult, options?: Partial<DxfOptions>): { sh: Sheet; yZero: number } {
  const o = { ...defaultDxfOptions(wall), ...options };
  const sh = new Sheet(o);
  const th = sh.th;
  const g = result.geometry;
  const poly = g.polygon;
  const Df = wall.geometry.embedmentDepth;
  const isRc = wall.geometry.mode === "rc-cantilever";
  const box = sceneBox(result, wall);
  const surf = backfillSurface(result, wall, box.maxX);
  const topPts = poly.filter((p) => Math.abs(p.y - g.yTop) < 1e-6);
  const topL = Math.min(...topPts.map((p) => p.x)), topR = Math.max(...topPts.map((p) => p.x));
  // Temel üstü kotu: betonarme taban plağı, taş temel bloğu (bodyBaseY) ya da 0
  const geo = wall.geometry;
  const yBaseTop = geo.mode === "rc-cantilever" ? rcFrame(geo).tf : geo.mode === "polygon" ? (geo.bodyBaseY ?? 0) : (geo.foundation?.height ?? 0);
  // Kot işaretleri: seçilen referans (ön zemin / taban / tepe) sıfır kabul edilir, üstüne mutlak kot (levelDatum) eklenir
  const yZero = o.levelRef === "base" ? 0 : o.levelRef === "top" ? g.yTop : Df;
  const elev = (y: number): number => Math.round((o.levelDatum + (y - yZero)) * 1000) / 1000;
  const tf = o.filterThickness;
  const dPipe = o.drain.enabled ? o.drain.diameter / 1000 : 0;
  const dBarb = o.barbacane.diameter / 1000;

  // ── 1. KESİT ────────────────────────────────────────────────────────────────
  // Filtre kaması: temel üstünden tepeye, arka yüze paralel şerit
  const backChain: Pt[] = [];
  {
    const iHeel = poly.findIndex((p) => Math.abs(p.x - g.heel.x) < 1e-9 && Math.abs(p.y - g.heel.y) < 1e-9);
    for (let k = 0; k < poly.length; k++) { const p = poly[(iHeel + k) % poly.length]!; backChain.push(p); if (p.y >= g.yTop - 1e-9) break; }
  }
  const chainAbove = backChain.filter((p) => p.y >= yBaseTop - 1e-9);
  if (chainAbove.length && chainAbove[0]!.y > yBaseTop + 1e-9) chainAbove.unshift({ x: xBackAt(poly, yBaseTop + 1e-6), y: yBaseTop });
  const filterBand: Pt[] = tf > 0 && chainAbove.length >= 2 ? [...chainAbove, ...[...chainAbove].reverse().map((p) => ({ x: p.x + tf, y: p.y }))] : [];
  const xBackBase = xBackAt(poly, yBaseTop + 1e-6);
  const pipeC = { x: xBackBase + 0.05 + dPipe / 2, y: yBaseTop + dPipe / 2 };
  const sandBed: Pt[] = dPipe > 0 ? [{ x: xBackBase, y: yBaseTop - 0.1 }, { x: xBackBase + Math.max(tf, dPipe + 0.2), y: yBaseTop - 0.1 }, { x: xBackBase + Math.max(tf, dPipe + 0.2), y: yBaseTop }, { x: xBackBase, y: yBaseTop }] : [];
  // Grobeton taban altında; eğik tabanda (topuk alçak) taban doğrusunu izler: y(x) = y_burun − tanα·(x − x_burun)
  const bsl = g.baseSlope ?? 0;
  const yBaseAt = (x: number): number => g.toe.y - bsl * (x - g.toe.x);
  const xbl0 = g.toe.x - o.blinding.overhang, xbl1 = g.heel.x + o.blinding.overhang, tb = o.blinding.thickness;
  const blinding: Pt[] = tb > 0 ? [{ x: xbl0, y: yBaseAt(xbl0) - tb }, { x: xbl1, y: yBaseAt(xbl1) - tb }, { x: xbl1, y: yBaseAt(xbl1) }, { x: xbl0, y: yBaseAt(xbl0) }] : [];
  const coping: Pt[] = o.coping.enabled ? [{ x: topL - o.coping.overhang, y: g.yTop }, { x: topR + o.coping.overhang, y: g.yTop }, { x: topR + o.coping.overhang, y: g.yTop + o.coping.height }, { x: topL - o.coping.overhang, y: g.yTop + o.coping.height }] : [];

  // Zemin taraması: istCAD paftalarındaki gibi yalnız yüzey boyunca bant (ön doğal zemin + arka dolgu yüzeyi), tüm alan doldurulmaz
  const left = box.minX - 1.0;
  const band = 0.4;
  if (Df > 1e-9) sh.hatch("ZEMIN", [[{ x: left, y: Df }, { x: xFrontAt(poly, Df + 1e-6), y: Df }, { x: xFrontAt(poly, Math.max(Df - band, 0) + 1e-6), y: Math.max(Df - band, 0) }, { x: left, y: Math.max(Df - band, 0) }]]);
  else sh.hatch("ZEMIN", [[{ x: left, y: 0 }, { x: g.toe.x, y: 0 }, { x: g.toe.x, y: -band }, { x: left, y: -band }]]);
  {
    const xBackTop = xBackAt(poly, g.yTop - 1e-6) + tf;
    const lower = [...surf].reverse().map((p) => ({ x: p.x, y: p.y - band }));
    sh.hatch("ZEMIN", [[{ x: xBackTop, y: g.yTop }, ...surf.slice(1), ...lower, { x: Math.max(xBackTop, xBackAt(poly, g.yTop - band + 1e-6) + tf), y: g.yTop - band }]]);
  }
  // Duvar
  sh.poly("DUVAR", poly, true);
  sh.hatch(isRc ? "BETON" : o.stone === "yonu" ? "KARGIR" : "MOLOZ", [poly]);
  if (g.key) { sh.poly("TEMEL", g.key, true); sh.hatch("BETON", [g.key]); }
  if (blinding.length) { sh.poly("GROBETON", blinding, true); sh.hatch("GROBETON", [blinding]); }
  if (coping.length) { sh.poly("HARPUSTA", coping, true); sh.hatch("BETON", [coping]); }
  // Zemin çizgileri
  sh.line("ZEMIN", { x: left, y: Df }, { x: xFrontAt(poly, Df + 1e-6), y: Df });
  sh.poly("ZEMIN", surf, false);
  if (wall.water.backLevel !== null) { sh.line("SU", { x: g.heel.x, y: wall.water.backLevel }, { x: box.maxX, y: wall.water.backLevel }); sh.text("SU", { x: box.maxX - 0.2, y: wall.water.backLevel + 0.05 }, "YASS", { halign: 2 }); }
  if (wall.water.frontLevel !== null) sh.line("SU", { x: left, y: wall.water.frontLevel }, { x: g.toe.x, y: wall.water.frontLevel });
  // Parsel sınırı: grobeton altından dolgu yüzeyinin üstüne kırmızı kesik-noktalı düşey çizgi + çizgi boyunca dışa bakan yazı
  const xPl = propertyLineX(geo, wall.propertyLine);
  if (xPl !== null) {
    const front = wall.propertyLine!.side === "front";
    const yTopPl = Math.max(g.yTop, ...surf.map((p) => p.y)) + 0.4;
    sh.line("PARSEL", { x: xPl, y: Math.min(g.toe.y, g.heel.y) - tb - 0.6 }, { x: xPl, y: yTopPl });
    sh.text("PARSEL", { x: xPl + (front ? -0.08 : 0.08 + th), y: (Df + g.yTop) / 2 }, "PARSEL SINIRI", { halign: 1, h: th * 0.8, rotation: 90 });
  }
  // Sağ taraftaki etiketler (barbakan ölçü zincirinin ötesinde) tek bir x'te hizalanır
  const xLabel = g.heel.x + Math.max(tf, 0.3) + 1.6;
  // Drenaj: filtre kaması (çakıl daireleri), geotekstil, boru, kum yatak
  if (filterBand.length) {
    sh.poly("DRENAJ", filterBand, true, { lineweight: 13 });
    // Çakıl: bant içinde deterministik yalancı-rastgele daireler
    let seed = 7;
    const rnd = (): number => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
    const yMin = chainAbove[0]!.y, yMax = Math.max(...chainAbove.map((p) => p.y));
    for (let y = yMin + 0.08; y < yMax - 0.05; y += 0.16) {
      const xb = xBackAt(poly, y);
      for (let x = xb + 0.08; x < xb + tf - 0.05; x += 0.16) sh.circle("DRENAJ", { x: x + (rnd() - 0.5) * 0.05, y: y + (rnd() - 0.5) * 0.05 }, 0.03 + rnd() * 0.02);
    }
    const outerEdge = [...chainAbove].map((p) => ({ x: p.x + tf, y: p.y }));
    sh.poly("GEOTEKSTIL", [{ x: chainAbove[0]!.x, y: yBaseTop }, ...outerEdge], false);
    const yF = yBaseTop + 0.45 * (yMax - yBaseTop);
    sh.leader({ x: xBackAt(poly, yF) + tf * 0.5, y: yF }, { x: xLabel, y: yF + 0.15 }, `Filtre: kırmataş / çakıl ${fmtCm(tf)} cm, geotekstil bohçalı`);
  }
  if (dPipe > 0) {
    sh.circle("DRENAJ", pipeC, dPipe / 2); sh.circle("DRENAJ", pipeC, dPipe / 2 - 0.006);
    if (sandBed.length) { sh.poly("DRENAJ", sandBed, true, { lineweight: 13 }); sh.hatch("KUM", [sandBed]); }
    sh.leader({ x: pipeC.x + dPipe / 2, y: pipeC.y }, { x: xLabel, y: pipeC.y - 0.3 }, `Drenaj borusu Ø${o.drain.diameter} mm delikli PVC, kum yatak 10 cm`, "DRENAJ");
  }
  // Barbakan: ön zeminden startHeight, sonra her vSpacing; eğik boru arka yüzden ön yüze
  const barbRows: number[] = [];
  if (o.barbacane.enabled) {
    for (let y = Df + o.barbacane.startHeight; y < g.yTop - 0.3; y += o.barbacane.vSpacing) barbRows.push(y);
    const a = (o.barbacane.angleDeg * Math.PI) / 180, dir = { x: -Math.cos(a), y: -Math.sin(a) }, n = { x: -Math.sin(a), y: Math.cos(a) };
    barbRows.forEach((yb, i) => {
      const entry = { x: xBackAt(poly, yb), y: yb };
      const exit = rayHit(poly, { x: entry.x - 1e-4, y: yb - 1e-4 * Math.tan(a) }, dir);
      if (!exit) return;
      const e0 = { x: entry.x + 0.03 * -dir.x, y: entry.y + 0.03 * -dir.y }, e1 = { x: exit.x + 0.03 * dir.x, y: exit.y + 0.03 * dir.y };   // uçlar yüzden 3 cm taşar
      // Gövde içindeki boru gizli çizgiyle (örnek DWG: GokHidden), uç kesitleri sürekli
      for (const s of [1, -1]) sh.line("BARBAKAN", { x: e0.x + s * n.x * dBarb / 2, y: e0.y + s * n.y * dBarb / 2 }, { x: e1.x + s * n.x * dBarb / 2, y: e1.y + s * n.y * dBarb / 2 }, { ltype: "HIDDEN" });
      sh.line("BARBAKAN", { x: e0.x + n.x * dBarb / 2, y: e0.y + n.y * dBarb / 2 }, { x: e0.x - n.x * dBarb / 2, y: e0.y - n.y * dBarb / 2 });
      sh.line("BARBAKAN", { x: e1.x + n.x * dBarb / 2, y: e1.y + n.y * dBarb / 2 }, { x: e1.x - n.x * dBarb / 2, y: e1.y - n.y * dBarb / 2 });
      // Her boruya ön yüzden kısa etiket: "%n eğimli / Ø…" (örnek DWG'deki GokPozGoster)
      sh.leader({ x: exit.x, y: exit.y }, { x: exit.x - 0.4, y: exit.y - 0.3 }, `Ø${o.barbacane.diameter} · %${Math.round(Math.tan(a) * 100)} eğimli`, "BARBAKAN");
      if (i === barbRows.length - 1) sh.leader({ x: entry.x + 0.02, y: entry.y }, { x: xLabel, y: g.yTop + 0.2 }, `Barbakan Ø${o.barbacane.diameter} mm PVC, ${o.barbacane.angleDeg}° eğim, ${fmt2(o.barbacane.hSpacing)} × ${fmt2(o.barbacane.vSpacing)} m şaşırtmalı`, "BARBAKAN");
    });
  }
  // Payanda izi (kesit arkasında)
  const cfProf = geo.mode === "rc-cantilever" && geo.counterforts ? rcCounterfortProfile(geo) : null;
  if (cfProf) sh.poly("PAYANDA", cfProf, true, { ltype: "HIDDEN", lineweight: 18 });
  // Kot işaretleri (±0,00 = ön doğal zemin)
  sh.level({ x: topR + (coping.length ? o.coping.overhang : 0) + 0.05, y: g.yTop + (coping.length ? o.coping.height : 0) }, elev(g.yTop + (coping.length ? o.coping.height : 0)), "right");
  sh.level({ x: left + 0.9, y: Df }, elev(Df));
  const xLevelL = g.toe.x - (2.25 + 0.35 * th / 0.125) - 0.35;   // H ölçüsünün solunda
  sh.level({ x: xLevelL, y: 0 }, elev(0));
  if (yBaseTop > 0) sh.level({ x: xLevelL, y: yBaseTop }, elev(yBaseTop));
  // Eğik taban: topuk alt kotu + taban eğimi yazısı (taban doğrusu boyunca, altında)
  if (bsl > 0) {
    sh.level({ x: g.heel.x + 0.15, y: g.heel.y }, elev(g.heel.y), "right");
    const xm = (g.toe.x + g.heel.x) / 2;
    sh.text("YAZI", { x: xm, y: yBaseAt(xm) - tb - 0.25 }, `TABAN EĞİMİ %${fmt2(bsl * 100).replace(/,00$/, "")} (TOPUK ALÇAK)`, { halign: 1, h: th * 0.8, rotation: (-Math.atan(bsl) * 180) / Math.PI });
  }
  sh.level({ x: box.maxX - 0.6, y: surf[surf.length - 1]!.y }, elev(surf[surf.length - 1]!.y), "right");
  // Ölçüler: ön zincirler (kademe/Df yakın, H uzak), taban altı zincir + B, tepe, arka
  const dims = dimensionLines(result, wall);
  const near = -(1.0 + 0.35 * th / 0.125), far = near - 0.9;   // (xLevelL = far − 0,35)
  const below = -(o.blinding.thickness + 0.55), belowB = below - 0.6;
  for (const d of dims) {
    const text = d.key === "H" || d.key === "B" || d.key === "Df" ? `${d.key} = ${o.unit === "cm" ? fmtCm(Math.hypot(d.b.x - d.a.x, d.b.y - d.a.y)) : fmt2(Math.hypot(d.b.x - d.a.x, d.b.y - d.a.y))}` : undefined;
    const vertical = Math.abs(d.a.x - d.b.x) < 1e-9;
    let off = d.offset;
    if (d.key === "H") off = far;
    else if (d.key === "Df") off = near;
    else if (d.key === "rc.hs" || /^s\d+\.h$/.test(d.key)) off = near - 0.45;
    else if (d.key === "B") off = belowB;
    else if (d.key === "rc.toe" || d.key === "rc.tb" || d.key === "rc.heel" || d.key === "f.toe" || d.key === "f.heel") off = below;
    else if (d.key === "top" || d.key === "rc.tt") off = 0.35 + (coping.length ? o.coping.height + 0.15 : 0);
    else if (d.key === "f.h" || d.key === "rc.tf") { d.a = { x: g.toe.x, y: d.a.y }; d.b = { x: g.toe.x, y: d.b.y }; off = -0.4; }
    else if (/^s\d+\.b$/.test(d.key)) off = -0.3;
    else if (d.key === "rc.key") off = 0.4;
    if (!vertical && Math.abs(d.a.y - d.b.y) > 1e-9) continue;   // yalnız yatay/düşey ölçüler
    sh.dimension(d.a, d.b, off, text);
  }
  if (barbRows.length) {
    const xd = g.heel.x + Math.max(tf, 0.3) + 1.1;
    sh.dimension({ x: xd, y: Df }, { x: xd, y: barbRows[0]! }, 0);
    for (let i = 1; i < barbRows.length; i++) sh.dimension({ x: xd, y: barbRows[i - 1]! }, { x: xd, y: barbRows[i]! }, 0);
    sh.line("OLCU", { x: g.heel.x + 0.1, y: Df }, { x: xd + 0.2, y: Df }, { lineweight: 9 });
  }
  if (coping.length) sh.dimension({ x: topR + o.coping.overhang, y: g.yTop }, { x: topR + o.coping.overhang, y: g.yTop + o.coping.height }, 0.3);
  if (wall.geometry.mode === "parametric" && wall.geometry.frontBatter > 0) {
    const mid = { x: xFrontAt(poly, g.yTop / 2), y: g.yTop / 2 };
    sh.text("YAZI", { x: mid.x - 0.12, y: mid.y }, `${Math.round(1 / wall.geometry.frontBatter)}:1`, { halign: 2, rotation: 90 - (Math.atan(wall.geometry.frontBatter) * 180) / Math.PI });
  }
  sh.text("YAZI", { x: box.maxX - 0.2, y: surf[surf.length - 1]!.y + 0.55 }, "DOLGU (YÜKSEK TOPRAK TARAFI)", { halign: 2, h: th * 0.9 });
  sh.text("YAZI", { x: left + 0.1, y: Df + 0.12 }, "DOĞAL ZEMİN", { h: th * 0.9 });
  const titleY = box.minY - 1.1;
  const title = (x: number, label: string, sub?: string): void => {
    sh.text("YAZI", { x, y: titleY }, `%%U${label}`, { h: th * 1.4, halign: 1 });   // %%U: AutoCAD altı çizili metin (örnek DWG'deki gibi)
    if (sub) sh.text("YAZI", { x, y: titleY - 0.55 }, sub, { h: th * 0.9, halign: 1 });
  };
  title((left + box.maxX) / 2, `A-A DUVAR KESİTİ — ÖLÇEK 1/${o.scale}`, `Duvar kesit alanı: ${fmt2(g.area)} m² (ölçüler ${o.unit})`);

  // ── 2. BARBAKAN YERLEŞİM PLANI (ön görünüş) ───────────────────────────────────
  sh.group = "gorunus";
  const L = o.wallLength;
  const ox = box.maxX + 2.5, oy = 0;
  const hTop = g.yTop + (coping.length ? o.coping.height : 0);
  sh.poly("DUVAR", [{ x: ox, y: oy + Df }, { x: ox + L, y: oy + Df }, { x: ox + L, y: oy + g.yTop }, { x: ox, y: oy + g.yTop }], true);
  if (coping.length) sh.poly("HARPUSTA", [{ x: ox, y: oy + g.yTop }, { x: ox + L, y: oy + g.yTop }, { x: ox + L, y: oy + hTop }, { x: ox, y: oy + hTop }], true);
  sh.poly("GIZLI", [{ x: ox, y: oy + Df }, { x: ox, y: oy }, { x: ox + L, y: oy }, { x: ox + L, y: oy + Df }], false);
  if (blinding.length) sh.poly("GIZLI", [{ x: ox, y: oy }, { x: ox, y: oy - o.blinding.thickness }, { x: ox + L, y: oy - o.blinding.thickness }, { x: ox + L, y: oy }], false);
  sh.line("ZEMIN", { x: ox - 0.6, y: oy + Df }, { x: ox + L + 0.6, y: oy + Df });
  let cols = 0;
  barbRows.forEach((yb, r) => {
    const shift = r % 2 ? o.barbacane.hSpacing / 2 : 0;
    for (let x = ox + o.barbacane.hSpacing / 2 + shift; x < ox + L - 0.2; x += o.barbacane.hSpacing) {
      // Daire + artı işareti (istCAD gösterimi) ve çap etiketi
      sh.circle("BARBAKAN", { x, y: oy + yb }, dBarb / 2); sh.circle("BARBAKAN", { x, y: oy + yb }, dBarb / 2 + 0.03);
      sh.line("BARBAKAN", { x: x - dBarb, y: oy + yb }, { x: x + dBarb, y: oy + yb }, { lineweight: 9 }); sh.line("BARBAKAN", { x, y: oy + yb - dBarb }, { x, y: oy + yb + dBarb }, { lineweight: 9 });
      sh.text("BARBAKAN", { x: x + dBarb * 1.2, y: oy + yb + dBarb * 0.6 }, `Ø${o.barbacane.diameter}`, { h: th * 0.7 });
      if (r === 0) cols++;
    }
  });
  for (let x = o.anoLength; x < L - 0.1; x += o.anoLength) { sh.line("DERZ", { x: ox + x, y: oy - (blinding.length ? o.blinding.thickness : 0) }, { x: ox + x, y: oy + hTop }); sh.text("DERZ", { x: ox + x + 0.08, y: oy + hTop - 0.4 }, "ANO / DİLATASYON DERZİ", { rotation: 90, h: th * 0.8 }); }
  sh.dimension({ x: ox, y: oy }, { x: ox + L, y: oy }, -(o.blinding.thickness + 0.6), `L = ${o.unit === "cm" ? fmtCm(L) : fmt2(L)}`);
  if (barbRows.length && cols >= 2) {
    const x0 = ox + o.barbacane.hSpacing / 2;
    // Yatay aralık ölçüsü ilk sıranın hemen üstünde (uzatma çizgileri kısa kalsın — istCAD paftası gibi)
    sh.dimension({ x: x0, y: oy + barbRows[0]! }, { x: x0 + o.barbacane.hSpacing, y: oy + barbRows[0]! }, 0.35);
    sh.dimension({ x: ox, y: oy + barbRows[0]! }, { x: x0, y: oy + barbRows[0]! }, 0.35);
    sh.dimension({ x: ox + L, y: oy + Df }, { x: ox + L, y: oy + barbRows[0]! }, 0.5);
    for (let i = 1; i < barbRows.length; i++) sh.dimension({ x: ox + L, y: oy + barbRows[i - 1]! }, { x: ox + L, y: oy + barbRows[i]! }, 0.5);
  }
  sh.dimension({ x: ox, y: oy }, { x: ox, y: oy + hTop }, -0.5, `${o.unit === "cm" ? fmtCm(hTop) : fmt2(hTop)}`);
  sh.level({ x: ox + L + 1.3, y: oy + Df }, elev(Df), "right");
  sh.level({ x: ox + L + 1.3, y: oy + hTop }, elev(hTop), "right");
  // A-A kesit işaretleri: ön görünüşün üstünde ve altında (çizgi + dolu üçgen + "A")
  {
    const xa = ox + Math.min(L * 0.3, 3.0), sA = th * 0.8;
    for (const [y0, d] of [[oy + hTop + 0.3, 1], [oy - (blinding.length ? o.blinding.thickness : 0) - 0.3, -1]] as const) {
      sh.line("KOT", { x: xa, y: y0 }, { x: xa, y: y0 + d * 0.8 }, { lineweight: 35 });
      sh.solid("KOT", [{ x: xa, y: y0 + d * 0.8 }, { x: xa - sA * 1.4, y: y0 + d * 0.8 + d * sA }, { x: xa - sA * 0.2, y: y0 + d * 0.8 + d * sA }]);
      sh.text("KOT", { x: xa + sA * 0.4, y: y0 + d * 0.8 + (d > 0 ? 0 : -sA * 1.6) }, "A", { h: sA * 1.6 });
    }
  }
  title(ox + L / 2, `DUVAR ÖNDEN GÖRÜNÜŞ VE BARBAKAN YERLEŞİMİ — ÖLÇEK 1/${o.scale}`, barbRows.length ? `Barbakan Ø${o.barbacane.diameter} mm, ${fmt2(o.barbacane.hSpacing)} × ${fmt2(o.barbacane.vSpacing)} m şaşırtmalı` : undefined);
  let rightX = ox + L + 4.0;

  // ── 2b. PLAN (payandalı duvar): u = duvar boyu → x, v = taban genişliği (burun altta) → y ──────
  if (cfProf && geo.mode === "rc-cantilever" && geo.counterforts) {
    sh.group = "plan";
    const f = rcFrame(geo), cf = geo.counterforts, px = rightX, py = 0;
    const xCfEnd = Math.max(...cfProf.map((p) => p.x));
    const rect = (u0: number, u1: number, v0: number, v1: number): Pt[] => [{ x: px + u0, y: py + v0 }, { x: px + u1, y: py + v0 }, { x: px + u1, y: py + v1 }, { x: px + u0, y: py + v1 }];
    sh.poly("DUVAR", rect(0, L, 0, f.B), true);
    sh.poly("DUVAR", rect(0, L, f.xStemFront, f.xStemBack), true);
    if (geo.key) sh.poly("GIZLI", rect(0, L, geo.key.offsetFromToe, geo.key.offsetFromToe + geo.key.width), true);
    const zs = rcCounterfortPositions(geo, L);
    for (const z of zs) { sh.poly("PAYANDA", rect(z - cf.thickness / 2, z + cf.thickness / 2, f.xStemBack, xCfEnd), true); sh.hatch("BETON", [rect(z - cf.thickness / 2, z + cf.thickness / 2, f.xStemBack, xCfEnd)]); }
    sh.dimension({ x: px, y: py }, { x: px + L, y: py }, -0.6, `L = ${o.unit === "cm" ? fmtCm(L) : fmt2(L)}`);
    if (zs.length) sh.dimension({ x: px, y: py + f.B }, { x: px + zs[0]!, y: py + f.B }, 0.4);
    if (zs.length > 1) sh.dimension({ x: px + zs[0]!, y: py + f.B }, { x: px + zs[1]!, y: py + f.B }, 0.4, `s = ${o.unit === "cm" ? fmtCm(cf.spacing) : fmt2(cf.spacing)}`);
    sh.dimension({ x: px, y: py }, { x: px, y: py + f.B }, -0.5, `B = ${o.unit === "cm" ? fmtCm(f.B) : fmt2(f.B)}`);
    sh.dimension({ x: px + L, y: py + f.xStemBack }, { x: px + L, y: py + xCfEnd }, 0.5);
    sh.text("YAZI", { x: px + L / 2, y: py + f.B + 1.0 }, "PAYANDA", { halign: 1, h: th * 0.9 });
    title(px + L / 2, "PLAN — PAYANDALAR");
    rightX = px + L + 2.5;
  }

  // ── 3. DONATI (betonarme) ─────────────────────────────────────────────────────
  if (isRc && result.rc) {
    sh.group = "donati";
    const rx = rightX;
    const shift = (p: Pt): Pt => ({ x: p.x + rx, y: p.y });
    sh.poly("DUVAR", poly.map(shift), true, { lineweight: 18 });
    if (g.key) sh.poly("TEMEL", g.key.map(shift), true, { lineweight: 18 });
    const items = rebarItems(wall, result);
    const rows: string[][] = [["Poz", "Konum", "Donatı", "Adet/m", "Boy (cm)", "kg/m"]];
    let total = 0;
    items.forEach((it, i) => {
      const poz = String(i + 1);
      if (it.path) sh.poly("DONATI", it.path.map(shift), false, { lineweight: 50 });
      if (it.dots) for (const p of it.dots) { sh.circle("DONATI", shift(p), 0.012); }
      const at = shift(it.labelAt);
      sh.circle("YAZI", { x: at.x, y: at.y + th * 0.4 }, th * 0.7); sh.text("YAZI", { x: at.x, y: at.y + th * 0.4 }, poz, { h: th * 0.8, halign: 1, valign: 2 });
      sh.text("DONATI", { x: at.x + th * 0.9, y: at.y }, it.label.replace("φ", "Ø"), { h: th * 0.9, halign: it.anchor === "end" ? 2 : 0 });
      const rItem = result.rc!.reinforcement.find((r) => r.position === (it.position === "stemMainCut" ? "stemMain" : it.position));
      const dia = rItem?.bar.dia ?? 0, spacing = rItem?.bar.spacing ?? 0.2;
      const len = it.path ? polyLength(it.path) : 1.0, count = it.path ? 1 / spacing : (it.dots?.length ?? 0);
      const kg = 0.00617 * dia * dia * len * count; total += kg;
      const sp = String(Math.round(spacing * 1000) / 10).replace(".", ",");
      rows.push([poz, rItem?.label ?? it.label.replace(/φ.*$/, "").trim(), `Ø${dia}/${sp}`, count.toFixed(1).replace(".", ","), fmtCm(len), kg.toFixed(1).replace(".", ",")]);
    });
    rows.push(["", "TOPLAM (1 m duvar boyu)", "", "", "", total.toFixed(1).replace(".", ",")]);
    // Tablo
    const widths = [0.6, 2.6, 1.1, 0.9, 1.0, 0.9], rowH = th * 2.2;
    const tx = rx + g.B + 1.0, ty = g.yTop;
    rows.forEach((r, ri) => {
      let x = tx; const y = ty - ri * rowH;
      r.forEach((cell, ci) => { sh.text(ri === 0 ? "YAZI" : "NOT", { x: x + 0.08, y: y - rowH + th * 0.5 }, cell, { h: th * 0.85 }); x += widths[ci]!; });
      sh.line("NOT", { x: tx, y: y - rowH }, { x: x, y: y - rowH }, { lineweight: ri === 0 || ri === rows.length - 2 ? 35 : 9 });
    });
    const tw = widths.reduce((s, v) => s + v, 0);
    sh.line("NOT", { x: tx, y: ty }, { x: tx + tw, y: ty }, { lineweight: 35 });
    let x = tx; for (const wv of [...widths, 0]) { sh.line("NOT", { x, y: ty }, { x, y: ty - rows.length * rowH }, { lineweight: 9 }); x += wv; }
    sh.text("YAZI", { x: tx, y: ty + 0.25 }, `DONATI TABLOSU — ${result.rc.materials.concrete} / ${result.rc.materials.steel}, pas payı ${fmtCm(result.rc.cover)} cm`, { h: th * 0.9 });
    title(rx + (g.B + 1.0 + tw) / 2, "DONATI KESİTİ VE TABLOSU");
    rightX = tx + tw + 1.5;
  }

  // ── 4. NOTLAR + ANTET ─────────────────────────────────────────────────────────
  sh.group = "notlar";
  const notes: string[] = ["GENEL NOTLAR"];
  const refLabel = o.levelRef === "base" ? "duvar temel taban kotu" : o.levelRef === "top" ? "duvar tepe kotu" : "duvar önü doğal zemin kotu";
  notes.push(`1. Ölçüler ${o.unit}, kotlar m'dir; ${o.levelDatum ? `${fmt2(o.levelDatum)} = ` : "±0,00 = "}${refLabel}${o.levelDatum ? " (mutlak kot)" : ""}. Kotlar mimari/vaziyet projesinden kontrol edilecektir.`);
  if (isRc) notes.push(`2. Beton ${result.rc?.materials.concrete ?? "C25/30"}, donatı ${result.rc?.materials.steel ?? "B420C"}, pas payı ${fmtCm(result.rc?.cover ?? 0.05)} cm. Temel altına ${fmtCm(o.blinding.thickness)} cm grobeton (C12/15) dökülecektir.`);
  else notes.push(`2. ${wall.material.description ?? "Harçlı moloz taş duvar"}, γ = ${fmt2(wall.material.gamma)} kN/m³; derzler tamamen harçla dolu, görünen yüzde derz ≤ 4 cm, düşey derzler şaşırtmalı (KGM Kısım 307). Temel altına ${fmtCm(o.blinding.thickness)} cm grobeton.`);
  if (o.barbacane.enabled) notes.push(`3. Barbakanlar Ø${o.barbacane.diameter} mm PVC, yatayda ${fmt2(o.barbacane.hSpacing)} m, düşeyde ${fmt2(o.barbacane.vSpacing)} m aralıkla şaşırtmalı; ilk sıra ön zeminden ${fmtCm(o.barbacane.startHeight)} cm yukarıda, öne doğru ${o.barbacane.angleDeg}° eğimli. Boru ağızları geotekstile sarılı çakılla bohçalanacaktır (DSİ §8).`);
  if (o.drain.enabled) notes.push(`4. Temel seviyesinde Ø${o.drain.diameter} mm delikli drenaj borusu duvar boyunca, ≥ %1 eğimle deşarja bağlanacak; kum yatak 10 cm. Arka yüzde ${fmtCm(tf)} cm kırmataş/çakıl filtre, dolguya karşı geotekstil ile ayrılacaktır (KGM Şekil-307-3).`);
  notes.push(`5. Her ${o.anoLength} m'de bir ano / dilatasyon derzi (2 cm, bitümlü dolgu); zemin değişikliğinde ek derz bırakılacaktır.`);
  if (o.coping.enabled) notes.push("6. Harpuşta yerinde dökme beton, iki yana eğimli ve damlalıklı (DSİ §10).");
  notes.push(`7. Hesap: sürşarj q = ${fmt2(wall.loads.surcharge)} kN/m², dolgu φ = ${fmt2(wall.backfill.layers[0]!.phi)}°, γ = ${fmt2(wall.backfill.layers[0]!.gamma)} kN/m³, zemin emniyet gerilmesi ${fmt2(wall.foundationSoil.bearing.sigmaAllow ?? 0)} kN/m²; ${result.codeLabel}. Farklı zemin koşulunda proje müellifine danışılacaktır.`);
  notes.push("8. Dolgu, duvar arkasında tabakalar hâlinde (≤ 30 cm) sıkıştırılarak yapılacak; sıkıştırma sırasında ağır makine duvara 1 m'den fazla yaklaşmayacaktır.");
  const notesY = titleY - 0.8, notesW = 12;
  sh.mtext("NOT", { x: left, y: notesY }, notesW, notes);
  // Antet (notların sağında)
  sh.group = "antet";
  const aw = 9.0, ah = 2.4, ax = Math.max(left + notesW + 1.0, rightX - aw), ay = notesY - ah;
  const rect = (x: number, y: number, w: number, h: number): Pt[] => [{ x, y }, { x: x + w, y }, { x: x + w, y: y + h }, { x, y: y + h }];
  sh.poly("ANTET", rect(ax, ay, aw, ah), true, { lineweight: 50 });
  const rowsA = 4, rh = ah / rowsA, split = aw * 0.68;
  for (let i = 1; i < rowsA; i++) sh.line("ANTET", { x: ax, y: ay + rh * i }, { x: ax + aw, y: ay + rh * i });
  sh.line("ANTET", { x: ax + split, y: ay }, { x: ax + split, y: ay + ah });
  const cell = (row: number, col: 0 | 1, value: string, h = th * 0.85): void => sh.text("ANTET", { x: ax + (col ? split : 0) + 0.15, y: ay + rh * (rowsA - 1 - row) + rh * 0.3 }, value, { h });
  cell(0, 0, o.projectName ?? "PROJE", th * 1.05); cell(0, 1, `ÖLÇEK 1/${o.scale}`, th * 1.05);
  cell(1, 0, `${wall.name} — ${isRc ? "BETONARME KONSOL İSTİNAT DUVARI" : "TAŞ İSTİNAT DUVARI"}: KESİT, GÖRÜNÜŞ, DETAYLAR`); cell(1, 1, `TARİH ${o.date ?? new Date().toISOString().slice(0, 10)}`);
  cell(2, 0, `H = ${fmt2(g.yTop)} m · B = ${fmt2(g.B)} m · Df = ${fmt2(Df)} m · ${result.codeLabel} · ${result.ok ? "tüm kontroller sağlıyor" : "kontrol sağlanmıyor (rapora bakınız)"}`, th * 0.72); cell(2, 1, `Hesap özeti ${o.digest ?? "—"}`);
  cell(3, 0, `TaşDuvar — hesap motoru v${result.engineVersion} · Bu çizim hesap raporuyla birlikte geçerlidir.`, th * 0.8); cell(3, 1, "PAFTA 1/1");
  return { sh, yZero };
}


/** Kot işareti öğeleri (Sheet.level ile aynı çizim): dolu üçgen + yatay çizgi + "+3,00" — pafta koordinatında, KOT katmanında */
export function levelPrims(p: Pt, value: number, th: number, side: "left" | "right" = "right"): Record<string, unknown>[] {
  const s = th * 0.8, dir = side === "left" ? -1 : 1;
  const label = `${value > 1e-9 ? "+" : value < -1e-9 ? "−" : "±"}${fmt2(Math.abs(value))}`;
  return [
    { k: "solid", layer: "KOT", pts: [p, { x: p.x + dir * s * 0.6, y: p.y + s }, { x: p.x - dir * s * 0.6, y: p.y + s }] },
    { k: "line", layer: "KOT", a: { x: p.x - dir * s * 0.6, y: p.y + s }, b: { x: p.x + dir * s * 3.6, y: p.y + s } },
    { k: "text", layer: "KOT", p: { x: p.x + dir * s * 0.8, y: p.y + s * 1.2 }, value: label, opts: { h: th * 0.9, halign: side === "left" ? 2 : 0 } },
  ];
}
